import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  VpnKey as VpnKeyIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import AppService from "../../../Configurations/Appservice";
import { useDispatch } from "react-redux";
import {
  updateScreenTitle,
  showSuccessMessage,
} from "../../../LayoutContainers/AppLayout/redux/AppSlice";

// Theme color matching sidebar
const THEME_COLOR = '#1a8a7e';
const THEME_COLOR_DARK = '#157a6f';
const THEME_GRADIENT = 'linear-gradient(135deg, #1a8a7e 0%, #2aa89a 100%)';

const ResetPasssword = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [touched, setTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    dispatch(updateScreenTitle("Reset Password"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError("");
    setSuccess(false);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const togglePasswordVisibility = (field) => () => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    if (!formData.currentPassword) {
      return "Current password is required";
    }
    if (!formData.newPassword) {
      return "New password is required";
    }
    if (formData.newPassword.length < 6) {
      return "New password must be at least 6 characters";
    }
    if (formData.newPassword === formData.currentPassword) {
      return "New password must be different from current password";
    }
    if (!formData.confirmPassword) {
      return "Please confirm your new password";
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return "New passwords do not match";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      await AppService.put("v1/auth/reset-password", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      
      setSuccess(true);
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTouched({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
      dispatch(showSuccessMessage("Password changed successfully!"));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = 
    formData.currentPassword && 
    formData.newPassword && 
    formData.confirmPassword && 
    formData.newPassword === formData.confirmPassword &&
    formData.newPassword !== formData.currentPassword &&
    formData.newPassword.length >= 6;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e0e0e0",
          overflow: "hidden",
          height: "100%",
          minHeight: 500,
        }}
      >
        <Grid container sx={{ height: "100%" }}>
          {/* Left Side - Branding */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              background: THEME_GRADIENT,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
              color: "#fff",
              minHeight: { xs: 200, md: "auto" },
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <SecurityIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}>
              Reset Password
            </Typography>
            <Typography
              variant="body1"
              sx={{ textAlign: "center", opacity: 0.9, maxWidth: 280 }}
            >
              Keep your account secure by updating your password regularly
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <CheckCircleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Minimum 6 characters
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <CheckCircleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Different from current password
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Confirm new password
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right Side - Form */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: { xs: 3, md: 5 },
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#1a1a2e" }}>
              Change Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Enter your current password and choose a new secure password
            </Typography>

            {success && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 3, borderRadius: 2 }}
              >
                Your password has been changed successfully!
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {/* Current Password */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#424242" }}>
                  Current Password
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showPasswords.current ? "text" : "password"}
                  placeholder="Enter your current password"
                  value={formData.currentPassword}
                  onChange={handleChange("currentPassword")}
                  onBlur={handleBlur("currentPassword")}
                  error={touched.currentPassword && !formData.currentPassword}
                  helperText={touched.currentPassword && !formData.currentPassword ? "Required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility("current")} edge="end" size="small">
                          {showPasswords.current ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              {/* New Password */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#424242" }}>
                  New Password
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showPasswords.new ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handleChange("newPassword")}
                  onBlur={handleBlur("newPassword")}
                  error={
                    touched.newPassword &&
                    (!formData.newPassword ||
                      formData.newPassword.length < 6 ||
                      formData.newPassword === formData.currentPassword)
                  }
                  helperText={
                    touched.newPassword && !formData.newPassword
                      ? "Required"
                      : touched.newPassword && formData.newPassword.length < 6
                      ? "Min 6 characters"
                      : touched.newPassword && formData.newPassword === formData.currentPassword
                      ? "Must be different from current"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility("new")} edge="end" size="small">
                          {showPasswords.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              {/* Confirm New Password */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#424242" }}>
                  Confirm New Password
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showPasswords.confirm ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  onBlur={handleBlur("confirmPassword")}
                  error={
                    touched.confirmPassword &&
                    (formData.confirmPassword !== formData.newPassword || !formData.confirmPassword)
                  }
                  helperText={
                    touched.confirmPassword && !formData.confirmPassword
                      ? "Required"
                      : touched.confirmPassword && formData.confirmPassword !== formData.newPassword
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility("confirm")} edge="end" size="small">
                          {showPasswords.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !isFormValid}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VpnKeyIcon />}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  bgcolor: THEME_COLOR,
                  color: "#ffffff",
                  boxShadow: "0 4px 14px rgba(26,138,126,0.4)",
                  "&:hover": {
                    bgcolor: THEME_COLOR_DARK,
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ResetPasssword;
